import React, { useState, useEffect } from 'react';
import { dataApi } from '../../services/api';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

// shadcn UI components
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

// Role definitions for team membership
// These correspond to Appwrite team membership roles used for RBAC
const ROLES = [
  {
    value: 'owner',
    label: 'Owner',
    description: 'Full system access including user management, settings, and billing'
  },
  {
    value: 'manager',
    label: 'Manager',
    description: 'Manage quotes, jobs, and customers; view reports'
  },
  {
    value: 'receptionist',
    label: 'Receptionist',
    description: 'Handle customer inquiries, create quotes, and manage bookings'
  },
  {
    value: 'tradesperson',
    label: 'Tradesperson',
    description: 'View and update assigned jobs and tasks'
  },
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Read-only access to quotes and customer information'
  }
];

/**
 * UserRolesForm - Dialog for editing team membership roles
 * 
 * Uses Appwrite team membership roles for RBAC. Roles are stored on the membership
 * and can only be updated by members with the 'owner' role.
 */
const UserRolesForm = ({ isOpen, onClose, user, onRolesUpdated }) => {
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize selected roles when user changes
  useEffect(() => {
    if (user && isOpen) {
      // Use teamRoles from membership
      const userRoles = user.teamRoles || user.roles || [];
      setSelectedRoles(Array.isArray(userRoles) ? userRoles : []);
      setError(null);
    }
  }, [user, isOpen]);

  const toggleRole = (role) => {
    setSelectedRoles(prev => {
      const newRoles = prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role];
      return newRoles;
    });
    // Clear error when user makes a selection
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedRoles.length === 0) {
      setError('At least one role must be selected');
      return;
    }

    try {
      setSaving(true);

      // Update membership roles using dataApi
      const membershipId = user.membershipId || user.$id;
      await dataApi.membershipUpdateRoles(membershipId, selectedRoles);

      toast.success('Roles updated successfully');
      
      // Call parent callback if provided
      if (onRolesUpdated) {
        onRolesUpdated({ ...user, teamRoles: selectedRoles, roles: selectedRoles });
      }

      // Close dialog
      onClose();
    } catch (error) {
      console.error('Error updating roles:', error);
      toast.error(error.message || 'Failed to update roles');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const userName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || user.email;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit User Roles</DialogTitle>
          <DialogDescription>
            Update roles for <strong>{userName}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Labels/Roles Selection */}
          <div className="space-y-2">
            <Label>
              Roles <span className="text-destructive">*</span>
            </Label>
            <p className="text-xs text-muted-foreground mb-3">
              Select one or more roles for this user
            </p>
            
            <div className={`border rounded-md ${error ? 'border-destructive' : ''}`}>
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="w-12 px-4 py-2"></th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Role</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {ROLES.map((roleDef) => (
                    <tr
                      key={roleDef.value}
                      className={`border-b last:border-b-0 hover:bg-muted/50 cursor-pointer transition-colors ${
                        selectedRoles.includes(roleDef.value) ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => toggleRole(roleDef.value)}
                    >
                      <td className="px-4 py-3 text-center">
                        <div className={`h-4 w-4 rounded-sm border-2 flex items-center justify-center ${
                          selectedRoles.includes(roleDef.value) 
                            ? 'bg-primary border-primary' 
                            : 'border-input'
                        }`}>
                          {selectedRoles.includes(roleDef.value) && (
                            <Check className="h-3 w-3 text-primary-foreground" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-sm">{roleDef.label}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {roleDef.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {error && (
              <p className="text-xs text-destructive">{error}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserRolesForm;
